/**
 * @file src/pages/device-settings/components/CreateDeviceModal.jsx
 * @description Modal for registering a new EGS device with ZATCA.
 *
 * Device creation must include branchId and must NOT include companyId.
 */

import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import SimpleSelect from "components/ui/SimpleSelect";
import ModalContainer from "components/ui/ModalContainer";
import Icon from "components/AppIcon";

const INVOICE_TYPES = [
  { value: "1100", labelKey: "deviceModals.create.options.standardInvoice" },
  { value: "0100", labelKey: "deviceModals.create.options.simplifiedInvoice" },
];

const ENVIRONMENTS = [
  { value: "sandbox", labelKey: "deviceModals.create.options.sandbox" },
  { value: "production", labelKey: "deviceModals.create.options.production" },
];

const INDUSTRIES = [
  { value: "Electronics", labelKey: "deviceModals.create.options.electronics" },
  { value: "Retail", labelKey: "deviceModals.create.options.retail" },
  { value: "Manufacturing", labelKey: "deviceModals.create.options.manufacturing" },
  { value: "Services", labelKey: "deviceModals.create.options.services" },
  { value: "Healthcare", labelKey: "deviceModals.create.options.healthcare" },
  { value: "Education", labelKey: "deviceModals.create.options.education" },
];

const CreateDeviceModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  error,
  company,
  branches = [],
}) => {
  const { t } = useTranslation("pages");
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const branchOptions = branches.map((branch) => ({
    value: branch._id || branch.id,
    label:
      branch.name ||
      branch.branchName ||
      branch.legalName ||
      t("deviceModals.create.branchFallback"),
  }));
  const invoiceTypeOptions = INVOICE_TYPES.map((option) => ({
    ...option,
    label: t(option.labelKey),
  }));
  const environmentOptions = ENVIRONMENTS.map((option) => ({
    ...option,
    label: t(option.labelKey),
  }));
  const industryOptions = INDUSTRIES.map((option) => ({
    ...option,
    label: t(option.labelKey),
  }));

  return (
    <ModalContainer isOpen={isOpen} onClose={onClose} maxWidth="max-w-2xl">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">
            {t("deviceModals.create.title")}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            disabled={isLoading}
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <Icon
                name="AlertCircle"
                size={16}
                className="text-red-600 me-2"
              />
              <p className="text-sm text-red-700">
                {error.message || t("deviceModals.create.errors.failedCreate")}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label={t("deviceModals.create.fields.branch")}>
              <SimpleSelect
                {...register("branchId", {
                  required: t("deviceModals.create.errors.branchRequired"),
                })}
                options={branchOptions}
                placeholder={
                  branchOptions.length
                    ? t("deviceModals.create.placeholders.branchSelect")
                    : t("deviceModals.create.placeholders.branchEmpty")
                }
                error={errors.branchId?.message}
                disabled={isLoading || branchOptions.length === 0}
              />
            </FormField>

            <FormField label={t("deviceModals.create.fields.deviceSerial")}>
              <Input
                {...register("deviceSerialNumber", {
                  required: t(
                    "deviceModals.create.errors.deviceSerialRequired",
                  ),
                })}
                placeholder={t("deviceModals.create.placeholders.deviceSerial")}
                error={errors.deviceSerialNumber?.message}
              />
            </FormField>

            <FormField label={t("deviceModals.create.fields.vatNumber")}>
              <Input
                {...register("vatNumber", {
                  required: t("deviceModals.create.errors.vatRequired"),
                  value: company?.vatNumber,
                  pattern: {
                    value: /^\d{15}$/,
                    message: t("deviceModals.create.errors.vatDigits"),
                  },
                })}
                placeholder={t("deviceModals.create.placeholders.vatNumber")}
                error={errors.vatNumber?.message}
              />
            </FormField>

            <FormField label={t("deviceModals.create.fields.organizationName")}>
              <Input
                {...register("organizationName", {
                  required: t(
                    "deviceModals.create.errors.organizationNameRequired",
                  ),
                })}
                placeholder={t(
                  "deviceModals.create.placeholders.organizationName",
                )}
                error={errors.organizationName?.message}
              />
            </FormField>

            <FormField label={t("deviceModals.create.fields.organizationUnit")}>
              <Input
                {...register("organizationUnitName", {
                  required: t(
                    "deviceModals.create.errors.organizationUnitRequired",
                  ),
                })}
                placeholder={t(
                  "deviceModals.create.placeholders.organizationUnit",
                )}
                error={errors.organizationUnitName?.message}
              />
            </FormField>

            <FormField label={t("deviceModals.create.fields.organizationIdentifier")}>
              <Input
                {...register("organizationIdentifier", {
                  required: t(
                    "deviceModals.create.errors.organizationIdentifierRequired",
                  ),
                  value: company?.vatNumber,
                  pattern: {
                    value: /^\d{15}$/,
                    message: t(
                      "deviceModals.create.errors.organizationIdentifierDigits",
                    ),
                  },
                })}
                placeholder={t(
                  "deviceModals.create.placeholders.organizationIdentifier",
                )}
                error={errors.organizationIdentifier?.message}
              />
            </FormField>

            <FormField label={t("deviceModals.create.fields.invoiceType")}>
              <SimpleSelect
                {...register("invoiceType", {
                  required: t("deviceModals.create.errors.invoiceTypeRequired"),
                })}
                options={invoiceTypeOptions}
                placeholder={t("deviceModals.create.placeholders.invoiceType")}
                error={errors.invoiceType?.message}
              />
            </FormField>

            <FormField label={t("deviceModals.create.fields.location")}>
              <Input
                {...register("location", {
                  required: t("deviceModals.create.errors.locationRequired"),
                })}
                placeholder={t("deviceModals.create.placeholders.location")}
                error={errors.location?.message}
              />
            </FormField>

            <FormField label={t("deviceModals.create.fields.industry")}>
              <SimpleSelect
                {...register("industry", {
                  required: t("deviceModals.create.errors.industryRequired"),
                })}
                options={industryOptions}
                placeholder={t("deviceModals.create.placeholders.industry")}
                error={errors.industry?.message}
              />
            </FormField>

            <FormField label={t("deviceModals.create.fields.countryCode")}>
              <Input
                {...register("countryCode", {
                  required: t("deviceModals.create.errors.countryRequired"),
                  value: "SA",
                })}
                value="SA"
                disabled
                placeholder="SA"
              />
            </FormField>

            <FormField label={t("deviceModals.create.fields.environment")}>
              <SimpleSelect
                {...register("environment", {
                  required: t("deviceModals.create.errors.environmentRequired"),
                })}
                options={environmentOptions}
                placeholder={t("deviceModals.create.placeholders.environment")}
                error={errors.environment?.message}
              />
            </FormField>
          </div>

          <div className="flex items-center justify-end gap-4 pt-6 border-t border-border">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={isLoading}
            >
              {t("common:actions.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isLoading || branchOptions.length === 0}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isLoading ? (
                <>
                  <Icon name="Loader" size={16} className="animate-spin" />
                  <span className="ms-2">
                    {t("deviceModals.create.actions.creating")}
                  </span>
                </>
              ) : (
                <>
                  <Icon name="Plus" size={16} />
                  <span className="ms-2">
                    {t("deviceModals.create.actions.create")}
                  </span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </ModalContainer>
  );
};

const FormField = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-foreground mb-2">
      {label}
    </label>
    {children}
  </div>
);

export default CreateDeviceModal;
